import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const usd = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Date ranges in the browser's local time (the store's time zone)
function rangeFor(key) {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  switch (key) {
    case 'today':     return [today, now];
    case 'yesterday': return [new Date(today.getTime() - 864e5), today];
    case '7d':        return [new Date(today.getTime() - 6 * 864e5), now];
    case '30d':       return [new Date(today.getTime() - 29 * 864e5), now];
    case 'month':     return [new Date(now.getFullYear(), now.getMonth(), 1), now];
    case 'lastmonth': return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    default:          return [today, now];
  }
}

const RANGES = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['month', 'This month'], ['lastmonth', 'Last month']];
const CHANNELS = [['', 'All'], ['website', '🌐 Website'], ['in_store', '🏪 In-store']];
const STATUS_BADGE = { COMPLETED: 'badge-green', APPROVED: 'badge-navy', PENDING: 'badge-gold', CANCELED: 'badge-gray', FAILED: 'badge-red' };

export default function AdminPayments() {
  const [range, setRange] = useState('7d');
  const [channel, setChannel] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [begin, end] = rangeFor(range);
    setLoading(true); setError('');
    try {
      const r = await api.get('/payments', { params: { begin: begin.toISOString(), end: end.toISOString() }, timeout: 60000 });
      setData(r.data);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Only owners and managers can view payments.' : (e.response?.data?.error || 'Could not load payments'));
      setData(null);
    }
    setLoading(false);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const payments = (data?.payments || []).filter(p => !channel || p.channel === channel);
  const ok = payments.filter(p => p.status === 'COMPLETED');
  const sum = (k) => ok.reduce((s, p) => s + p[k], 0);
  const gross = sum('total'), refunded = sum('refunded'), fees = sum('fee');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Payments</h1>
          <p style={{ color: '#9a9a9a', fontSize: 13 }}>Card payments from Square — website checkouts and the in-store reader</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="form-control" style={{ width: 'auto' }} value={range} onChange={e => setRange(e.target.value)}>
            {RANGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div style={{ display: 'flex', background: '#fff', borderRadius: 8, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
            {CHANNELS.map(([k, l]) => (
              <button key={k} onClick={() => setChannel(k)}
                style={{ padding: '8px 12px', border: 'none', background: channel === k ? '#1a2744' : 'transparent', color: channel === k ? '#fff' : '#555', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{l}</button>
            ))}
          </div>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>{loading ? 'Loading…' : '↻ Refresh'}</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              ['Sales', gross, `${ok.length} payment${ok.length === 1 ? '' : 's'}`],
              ['Refunds', -refunded, ''],
              ['Square fees', -fees, ''],
              ['Net deposit', gross - refunded - fees, 'what Square pays out'],
            ].map(([label, cents, sub]) => (
              <div key={label} className="card" style={{ padding: '16px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: label === 'Net deposit' ? '#2e7d32' : '#1a2744', marginTop: 4 }}>{cents < 0 ? `−${usd(-cents)}` : usd(cents)}</div>
                {sub && <div style={{ fontSize: 12, color: '#9a9a9a' }}>{sub}</div>}
              </div>
            ))}
          </div>
          {data.truncated && <div className="alert alert-info">Showing the most recent 1,000 payments — pick a shorter range to see everything.</div>}

          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
              <thead>
                <tr style={{ background: '#f8f7f2', textAlign: 'left' }}>
                  {['When', 'Where', 'Card', 'Amount', 'Fee', 'Refunded', 'Status', 'Order / Receipt'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: '#9a9a9a' }}>No payments in this range.</td></tr>}
                {payments.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid #f0f0f0', opacity: p.status === 'COMPLETED' ? 1 : 0.6 }}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{new Date(p.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                    <td style={{ padding: '10px 14px' }}>{p.channel === 'website' ? '🌐 Website' : '🏪 In-store'}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{p.card_brand ? `${p.card_brand.replace(/_/g, ' ')} •••• ${p.last4}` : (p.source_type || '—').toLowerCase()}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700 }}>{usd(p.total)}{p.tip > 0 && <span style={{ fontWeight: 400, color: '#9a9a9a' }}> (tip {usd(p.tip)})</span>}</td>
                    <td style={{ padding: '10px 14px', color: '#666' }}>{p.fee ? usd(p.fee) : '—'}</td>
                    <td style={{ padding: '10px 14px', color: p.refunded ? '#c62828' : '#9a9a9a' }}>{p.refunded ? usd(p.refunded) : '—'}</td>
                    <td style={{ padding: '10px 14px' }}><span className={`badge ${STATUS_BADGE[p.status] || 'badge-gray'}`} style={{ fontSize: 10 }}>{p.status}</span></td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {p.order && <Link to="/admin/orders" style={{ color: '#c9a227', fontWeight: 700, fontFamily: 'monospace', marginRight: 10 }}>{p.order.order_number}{p.order.shipping_method === 'pickup' ? ' 🏪' : ''}</Link>}
                      {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" style={{ color: '#666' }}>Receipt ↗</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: '#9a9a9a', marginTop: 10 }}>
            Totals count completed payments only. Fees can take a few minutes to appear on new payments. Issue refunds in the Square Dashboard.
          </p>
        </>
      )}
      {!data && loading && <div className="loading"><div className="spinner" /></div>}
    </div>
  );
}
